/**
 * Zwei Schreibweisen zu einer machen.
 *
 * Das ist der heikelste Vorgang im ganzen Backoffice, deshalb ist die
 * Oberfläche hier absichtlich langsam: Sie schlägt zuerst die ähnlichsten
 * Einträge vor (dieselbe Rechnung wie in der App), zeigt danach den Satz aus,
 * der beschreibt was passiert — „X verschwindet, Y bleibt" —, und lässt erst
 * dann drücken.
 *
 * Warum so viel Umstand: Der Vorgang hängt alles um, was am verschwindenden
 * Eintrag hing, und lässt sich nicht mit einem Klick rückgängig machen. Wer
 * die Richtung verwechselt, hat den falschen Namen im Bestand — und merkt es
 * erst, wenn eine Rechnung komisch aussieht.
 *
 * Rückgängig geht trotzdem nicht ins Leere: Die alte Zeile bleibt stillgelegt
 * stehen und der alte Name wandert in die Schreibweisen des Ziels. Verloren
 * ist nichts, aber Zurückdrehen ist Handarbeit in der Datenbank.
 */

import { useMemo, useState } from 'react';
import { Karte, Knopf, Marker, Meldung, Suchleiste } from '../gestaltung/bausteine';
import { aehnlicheTreffer, passt } from '../daten/textvergleich';

type Eintrag = { id: string; name: string; aktiv?: boolean };

// Bewusst NICHT über die vollen Typen von Ort und Kunde generisch: Gebraucht
// werden nur Kennung und Name. So kann die Nachpflegeliste dieselbe Maske
// benutzen, obwohl ihre Einträge aus einer Datenbanksicht kommen und mit den
// Stammdatentypen nichts gemein haben.
export function ZusammenfuehrenFormular(eigenschaften: {
  /** Der Eintrag, der verschwindet. */
  quelle: Eintrag;
  /** Alle infrage kommenden Ziele — die Quelle wird selbst herausgefiltert. */
  auswahl: Eintrag[];
  wortFuerEinen: string;
  laeuft: boolean;
  abbrechen: () => void;
  bestaetigen: (zielId: string) => void;
}) {
  const [suche, setSuche] = useState('');
  const [ziel, setZiel] = useState<Eintrag | null>(null);

  const moeglich = useMemo(
    () => eigenschaften.auswahl.filter((e) => e.id !== eigenschaften.quelle.id),
    [eigenschaften.auswahl, eigenschaften.quelle.id],
  );

  // Ohne Sucheingabe zeigen wir nicht die ganze Liste, sondern die
  // wahrscheinlichen Kandidaten. Genau die sind fast immer gemeint — und eine
  // Liste mit hundertachtundzwanzig Orten lädt zum Danebengreifen ein.
  const vorschlaege = useMemo(
    () => aehnlicheTreffer(moeglich, eigenschaften.quelle.name, 5),
    [moeglich, eigenschaften.quelle.name],
  );

  const treffer = useMemo(() => {
    if (!suche.trim()) return vorschlaege;
    return moeglich.filter((e) => passt(e.name, suche)).slice(0, 12);
  }, [moeglich, suche, vorschlaege]);

  return (
    <Karte titel={`„${eigenschaften.quelle.name}“ zusammenführen`}>
      <div className="stapel" style={{ maxWidth: 640 }}>
        <p className="meta">
          Suche den Eintrag, der bleiben soll. Alles, was an „
          {eigenschaften.quelle.name}“ hängt — Fahrten, Aufträge, Vorlagen —
          hängt danach dort.
        </p>

        <Suchleiste
          wert={suche}
          setzen={setSuche}
          platzhalter={`${eigenschaften.wortFuerEinen} suchen …`}
        />

        {!suche.trim() && vorschlaege.length > 0 && (
          <p className="meta">Ähnliche Schreibweisen zuerst:</p>
        )}

        {treffer.length === 0 ? (
          <Meldung art="neutral">
            {suche.trim()
              ? 'Nichts gefunden. Tippe anders oder brich ab.'
              : `Es gibt keinen anderen ${eigenschaften.wortFuerEinen} zum Zusammenführen.`}
          </Meldung>
        ) : (
          <div className="stapel" style={{ gap: 4 }}>
            {treffer.map((e) => (
              <button
                key={e.id}
                className={`wahlzeile${ziel?.id === e.id ? ' wahlzeile--gewaehlt' : ''}`}
                onClick={() => setZiel(e)}
              >
                <span>{e.name}</span>
                {e.aktiv === false && <Marker art="neutral">stillgelegt</Marker>}
              </button>
            ))}
          </div>
        )}

        {ziel && (
          <Meldung art="warnung" titel="Das passiert beim Zusammenführen">
            <strong>„{eigenschaften.quelle.name}“ verschwindet</strong> aus den
            Auswahllisten und bleibt nur noch als alte Schreibweise auffindbar.{' '}
            <strong>„{ziel.name}“ bleibt</strong> und übernimmt alles, was daran
            hing. Das lässt sich nicht mit einem Klick zurückdrehen.
          </Meldung>
        )}

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abbrechen}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!ziel || eigenschaften.laeuft}
            onClick={() => ziel && eigenschaften.bestaetigen(ziel.id)}
          >
            {eigenschaften.laeuft
              ? 'Wird zusammengeführt …'
              : ziel
                ? `Auf „${ziel.name}“ zusammenführen`
                : 'Erst ein Ziel wählen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
